import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle } from 'lucide-react';

const WARNING_LABELS = {
  missing_customer_address: 'Morada do cliente em falta',
  missing_customer_city: 'Cidade do cliente em falta',
  missing_customer_email: 'Email do cliente em falta',
  missing_customer_nuit_fiscal: 'NUIT do cliente em falta (documento fiscal)',
  missing_customer_nuit_company: 'NUIT obrigatório para Empresas em falta',
  missing_customer_phone: 'Contacto/telefone do cliente em falta',
  missing_customer_contact_person: 'Pessoa de contacto em falta',
  missing_supplier_tax_id: 'NUIT do fornecedor em falta',
  missing_supplier_address: 'Morada do fornecedor em falta',
  missing_supplier_city: 'Cidade do fornecedor em falta',
  missing_supplier_contact_person: 'Pessoa de contacto do fornecedor em falta',
};

export default function MissingMasterDataDialog({
  open,
  onOpenChange,
  warnings = [],
  customerName,
  entityName,
  entityTypeLabel = 'Cliente',
  editEntityLabel,
  onEditClient,
  onEditEntity,
  onPrintAnyway,
}) {
  const [confirmCompanyNuit, setConfirmCompanyNuit] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const isCompanyNuitMissing = warnings.includes('missing_customer_nuit_company');

  useEffect(() => {
    if (open) {
      setConfirmCompanyNuit(false);
      setOverrideReason('');
    }
  }, [open]);

  const warningList = Array.from(new Set(warnings)).map((code) => ({
    code,
    label: WARNING_LABELS[code] || code,
  }));

  const subjectName = entityName || customerName;
  const subjectLabelLower = (entityTypeLabel || 'Cliente').toLowerCase();
  const editHandler = onEditEntity || onEditClient;
  const editButtonLabel = editEntityLabel || `Editar ${entityTypeLabel || 'Cliente'}`;

  const canPrint = !isCompanyNuitMissing || (confirmCompanyNuit && overrideReason.trim().length >= 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="print-missing-data-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Dados Mestre em Falta
          </DialogTitle>
          <DialogDescription>
            {subjectName
              ? `${entityTypeLabel}: ${subjectName}`
              : `Complete os dados mestre do ${subjectLabelLower} para os documentos.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-800">Campos em falta:</p>
            <ul className="mt-2 space-y-1 text-sm text-amber-800" data-testid="print-missing-data-list">
              {warningList.map((item) => (
                <li key={item.code} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-600" />
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {isCompanyNuitMissing && (
            <Alert className="border-red-300 bg-red-50" data-testid="print-missing-data-strong-warning">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-sm text-red-700">
                <strong>Atenção:</strong> Empresa sem NUIT. Confirme explicitamente antes de imprimir.
              </AlertDescription>
            </Alert>
          )}

          {isCompanyNuitMissing && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="confirm-nuit"
                  checked={confirmCompanyNuit}
                  onCheckedChange={(value) => setConfirmCompanyNuit(!!value)}
                  data-testid="print-missing-data-confirm-checkbox"
                />
                <Label htmlFor="confirm-nuit" className="text-sm">
                  Confirmo que quero imprimir sem NUIT (Empresa)
                </Label>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Motivo da impressão *</Label>
                <Textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Descreva o motivo (mín. 5 caracteres)"
                  rows={3}
                  data-testid="print-missing-data-reason-input"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={editHandler}
            data-testid="print-missing-data-edit-client-btn"
          >
            {editButtonLabel}
          </Button>
          <Button
            onClick={() => onPrintAnyway?.(overrideReason)}
            disabled={!canPrint}
            data-testid="print-missing-data-confirm-btn"
          >
            Imprimir mesmo assim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
